/**
 * ServiceHistoryList Component
 * Displays service history for a vehicle with add/edit/delete functionality
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { EmptyState } from '../ui/empty-state';
import { LoadingState } from '../ui/loading-state';
import { ErrorState } from '../ui/error-state';
import { ServiceHistoryModal } from './ServiceHistoryModal';
import {
  getVehicleHistory,
  deleteHistoryEntry,
  type ServiceHistoryEntry,
} from '../../services/serviceHistoryApi';
import { useToast } from '../../contexts/ToastContext';

interface ServiceHistoryListProps {
  vehicleId: string;
  vehiclePlate?: string;
}

function normalizeDate(value: ServiceHistoryEntry['performedAt']) {
  if (value instanceof Date) return value;
  const num = Number(value);
  if (!Number.isNaN(num)) {
    // If the value looks like seconds, convert to ms
    return new Date(num < 10_000_000_000 ? num * 1000 : num);
  }
  return new Date(value);
}

export function ServiceHistoryList({ vehicleId, vehiclePlate }: ServiceHistoryListProps) {
  const [entries, setEntries] = useState<ServiceHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<ServiceHistoryEntry | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<ServiceHistoryEntry | null>(null);
  const toast = useToast();

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getVehicleHistory(vehicleId);
      setEntries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar historial');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleAdd = () => {
    setSelectedEntry(null);
    setIsModalOpen(true);
  };

  const handleEdit = (entry: ServiceHistoryEntry) => {
    setSelectedEntry(entry);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (entry: ServiceHistoryEntry) => {
    setEntryToDelete(entry);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return;

    try {
      await deleteHistoryEntry(vehicleId, entryToDelete.id);
      toast.success('Registro eliminado correctamente');
      loadHistory();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al eliminar');
    }
  };

  const handleModalClose = (saved: boolean) => {
    setIsModalOpen(false);
    setSelectedEntry(null);
    if (saved) {
      loadHistory();
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(cents / 100);
  };

  const renderSourceBadge = (source: ServiceHistoryEntry['source']) => {
    if (source === 'case') return <Badge variant="outline">Caso</Badge>;
    if (source === 'system') return <Badge variant="secondary">Automático</Badge>;
    return <Badge variant="outline">Manual</Badge>;
  };

  if (loading) {
    return <LoadingState text="Cargando historial de servicio..." />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={loadHistory} />;
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            Historial de Servicio
            {vehiclePlate && <span className="ml-2 text-muted-foreground">({vehiclePlate})</span>}
          </CardTitle>
          <Button onClick={handleAdd}>Agregar Registro</Button>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <EmptyState
              title="Sin historial"
              description="Este vehículo no tiene registros de servicio aún."
              action={
                <Button variant="outline" onClick={handleAdd}>
                  Agregar primer registro
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo de Servicio / Caso</TableHead>
                  <TableHead>Técnico</TableHead>
                  <TableHead className="text-right">Costo</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => {
                  const isCase = entry.source === 'case';
                  const performedDate = normalizeDate(entry.performedAt);
                  const formattedDate = format(performedDate, "d 'de' MMMM, yyyy", { locale: es });

                  return (
                    <TableRow key={entry.id}>
                      <TableCell>{formattedDate}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <span>{entry.serviceTypeName || 'Servicio'}</span>
                          {entry.meta?.caseStatus && (
                            <span className="text-xs text-muted-foreground">
                              Estado: {entry.meta.caseStatus}
                            </span>
                          )}
                          {entry.meta?.bookingCount !== undefined && entry.source === 'case' && (
                            <span className="text-[11px] text-muted-foreground">
                              {entry.meta.bookingCount} cita(s)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{entry.technicianName || (isCase ? '—' : '-')}</TableCell>
                      <TableCell className="text-right">
                        {entry.cost ? formatCurrency(entry.cost) : isCase ? '—' : '-'}
                      </TableCell>
                      <TableCell>{renderSourceBadge(entry.source)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {isCase ? (
                            entry.meta?.caseId ? (
                              <Link to={`/cases/${entry.meta.caseId}`} className="text-primary text-sm hover:underline">
                                Ver caso
                              </Link>
                            ) : (
                              <span className="text-xs text-muted-foreground">Caso</span>
                            )
                          ) : (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(entry)}>
                                Editar
                              </Button>
                              {entry.source === 'manual' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteClick(entry)}
                                >
                                  Eliminar
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ServiceHistoryModal
        vehicleId={vehicleId}
        entry={selectedEntry}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Eliminar registro"
        description="¿Estás seguro de que deseas eliminar este registro de servicio? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
